<script lang="ts">
// the users' rows and their header share this grid: look as, name, e-mail, role, instelling/thema, details pill, delete
export const USER_COLUMNS: string = '2rem minmax(12rem, 1fr) minmax(12rem, 1fr) 12.5rem minmax(12rem, 1fr) 6rem 2rem';
</script>

<script setup lang="ts">
import {computed, nextTick, type PropType, ref, toRaw} from 'vue';
import {USER_CHANGE_EMIT, USER_REMOVE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {CabOrganization, CabTheme, type CabUser, isValidEmail, landingVan, permissiesVan, Role, ROLLEN_MET_INSTELLING, type SessieTelling, uuidOf, type WelkomStatus} from 'common';
import UserAuthDialog from '@/views/admin/UserAuthDialog.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import StatusTag from '@/views/ui/StatusTag.vue';
import {BadgeCheck, Eye, Gem, Monitor, ShieldCheck, Trash2} from 'lucide-vue-next';
import {CascaderOption, ElCascader} from 'element-plus';
import {roleLabel} from '@/base/utils';
import {impersoneer} from '@/base/callBackend';
import {clearMe} from '@/base/me.js';

const emits = defineEmits([USER_REMOVE_EMIT, USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT]);
const props = defineProps({
    user: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    allOrganizations: {
        type    : Object as PropType<CabOrganization[]>,
        required: true,
    },
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
        default : Object.values(Role),
    },
    me: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    orgThemeOptions: {
        type    : Object as PropType<CascaderOption[]>,
        required: true,
    },
    allUsers: {
        type    : Object as PropType<CabUser[]>,
        required: true,
    },
    telling: {
        type    : Object as PropType<SessieTelling>,
        required: false,
    },
    welkom: {
        type    : String as PropType<WelkomStatus>,
        required: false,
    },
});
defineExpose({
    firstEdit,
});

const user             = ref<CabUser>(props.user);
const nameField        = ref<HTMLInputElement | null>(null);
const nameEditing      = ref<boolean>(false);
const emailField       = ref<HTMLInputElement | null>(null);
const emailEditing     = ref<boolean>(false);
const authDialog       = ref<boolean>(false);
const orgThemeSelected = ref(props.user?.getOrgThemeCombiUuids());
const heeftInstelling  = computed<boolean>(() => user.value.roles.some(r => ROLLEN_MET_INSTELLING.includes(r)));
const roleOptions      = computed<{ label: string; value: string }[]>(() =>
    props.selectableRoles.map((role: Role) => ({
        label: roleLabel(role),
        value: role,
    })),
);

// the gem: amber = the welcome-mail button mails this user, grey = not yet (account incomplete), blue = mailed, link open
const WELKOM_KIND: Record<WelkomStatus, 'ok' | 'warn' | 'bad' | 'info'> = {
    'kandidaat' : 'warn',
    'incompleet': 'info',
    'open-link' : 'ok',
    'wachtwoord': 'ok',
};
const WELKOM_TITLE: Record<WelkomStatus, string>                        = {
    'kandidaat' : 'nog geen wachtwoord: krijgt een welkomstmail bij de mailknop',
    'incompleet': 'nog geen wachtwoord en nog geen welkomstmail: het account is nog niet compleet',
    'open-link' : 'welkomstmail verstuurd, de link is nog open',
    'wachtwoord': 'heeft een wachtwoord',
};

// what an incomplete account still lacks: the same rule as welkomStatusVan in the backend (instelling/thema only for a reporter)
const ontbreekt = computed<string[]>(() => {
    const uit: string[] = [];
    if (!user.value.email || !isValidEmail(user.value.email)) {
        uit.push('een geldig e-mailadres');
    }
    if (user.value.roles.length === 0) {
        uit.push('een rol');
    }
    if (user.value.roles.includes(Role.reporter) && (user.value.organizations.length === 0 || user.value.themes.length === 0)) {
        uit.push('instelling/thema');
    }
    return uit;
});

const welkomTitle = computed<string>(() => {
    if (props.welkom === 'incompleet' && ontbreekt.value.length > 0) {
        const lijst: string = ontbreekt.value.length === 1 ? ontbreekt.value[0] : `${ontbreekt.value.slice(0, -1).join(', ')} en ${ontbreekt.value[ontbreekt.value.length - 1]}`;
        return `nog geen wachtwoord en nog geen welkomstmail: mist ${lijst}`;
    }
    return props.welkom ? WELKOM_TITLE[props.welkom] : '';
});

const sessieTitle = computed<string>(() => {
    if (!props.telling || (props.telling.sessies === 0 && props.telling.browsers === 0)) {
        return 'geen actieve sessies';
    }
    return `${metAantal(props.telling.sessies, 'sessie', 'sessies')}, ${metAantal(props.telling.browsers, 'vertrouwde browser', 'vertrouwde browsers')}`;
});

const isNameDuplicate = computed<boolean>(() => {
    const naam: string = (user.value.name ?? '').toLowerCase().trim();
    return naam !== '' && props.allUsers.filter((u: CabUser) => (u.name ?? '').toLowerCase().trim() === naam).length > 1;
});

const isEmailDuplicate = computed<boolean>(() => {
    if (!user.value.email || !user.value.email.trim()) {
        return false;
    }
    const currentEmail = user.value.email.toLowerCase().trim();
    const duplicateCount = props.allUsers.filter(u =>
        u.email && u.email.toLowerCase().trim() === currentEmail
    ).length;
    return duplicateCount > 1;
});
// only the shape (x@y.z) is checked, the same rule the backend applies before it mails; empty stays a placeholder
const isEmailInvalid = computed<boolean>(() => !!user.value.email && !isValidEmail(user.value.email));

// only an admin, only a stored user, never an admin, never a user without a landing page (the backend refuses those too)
const kanImpersoneren = computed<boolean>(() => props.me.roles.includes(Role.admin) && !!user.value.uuid && user.value.uuid !== props.me.uuid && !user.value.roles.includes(Role.admin)
                                                 && landingVan(permissiesVan(user.value.roles)) !== undefined);

let isFirstEdit: boolean = false;

//==========================================
function metAantal(n: number, enkelvoud: string, meervoud: string): string {
    return `${n} ${n === 1 ? enkelvoud : meervoud}`;
}

//==========================================
function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur();
    if (isFirstEdit) {
        if (!props.user.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

//==========================================
function startEditingEmail() {
    emailEditing.value = true;
    nextTick(() => emailField.value!.focus());
}

function stopEditingEmail() {
    emailEditing.value = false;
    emailField.value!.blur();
}

async function onImpersoneer() {
    await impersoneer(user.value.uuid);
    clearMe();
    window.location.assign('/');
}

//==========================================
function onDelete() {
    emits(USER_REMOVE_EMIT, user.value);
}

function onChangeOrgTheme() {
    // an empty set is allowed: a reporter without instelling/thema sees nothing and gets no welcome mail yet
    const rawSelected: string[][] = toRaw(orgThemeSelected.value).map(path => toRaw(path));
    const newOrgs: CabOrganization[] = [];
    const newThemes: CabTheme[]      = [];
    rawSelected.forEach(([oUuid, tUuid]) => {
        const o = props.allOrganizations!.find((o) => uuidOf(o) === oUuid)!;
        const t = o.themes.find((t) => uuidOf(t) === tUuid)!;
        newOrgs.push(o);
        newThemes.push(t);
    });
    Object.assign(user.value, {
        organizations: newOrgs,
        themes       : newThemes,
    });
    onChange();
}

function onChange() {
    // Ensure roles is always a single-value array (if not empty)
    if (user.value.roles) {
        if (Array.isArray(user.value.roles)) {
            user.value.roles = user.value.roles.length > 0 ? [user.value.roles[0]] : [];
        } else if (typeof user.value.roles === 'string') {
            user.value.roles = [user.value.roles];
        }
    }
    emits(USER_CHANGE_EMIT, user.value);
}

//==========================================
function onSessionsEnded() {
    emits(USER_SESSIONS_ENDED_EMIT);
}

function onEmailChange() {
    user.value.email = user.value.email ? user.value.email.toLowerCase().trim() : '';
    onChange();
}
</script>

<template>
    <RowGrid :columns="USER_COLUMNS" :data-email="user.email" :class="{'relative isolate before:absolute before:inset-1 before:-z-10 before:rounded-control before:bg-[var(--zzc-tag-info-bg)]': user.medewerker}" data-testid="user-strip">
        <IconButton
            v-if="kanImpersoneren"
            :icon="Eye"
            label="bekijk als deze gebruiker"
            size="sm"
            data-testid="impersoneer-button"
            @click="onImpersoneer()"
        />
        <span v-else/>

        <input
            class="row-input"
            :class="{'row-input-on': nameEditing, 'row-input-error': isNameDuplicate}"
            type="text"
            ref="nameField"
            v-model="user.name"
            @input="onChange"
            @click="startEditingName()"
            @focus="startEditingName()"
            @keyup.enter="stopEditingName()"
            @blur="stopEditingName()"
            placeholder="naam"
            :title="isNameDuplicate ? 'deze naam is al in gebruik' : user.name"
            :disabled="user.medewerker"
            data-testid="user-name"
        />

        <input
            class="row-input"
            :class="{'row-input-on': emailEditing, 'row-input-error': isEmailDuplicate || isEmailInvalid}"
            type="text"
            ref="emailField"
            v-model="user.email"
            @input="onEmailChange"
            @click="startEditingEmail()"
            @focus="startEditingEmail()"
            @keyup.enter="stopEditingEmail()"
            @blur="stopEditingEmail()"
            placeholder="e-mail"
            :title="isEmailInvalid ? 'ongeldig e-mailadres' : isEmailDuplicate ? 'dit e-mailadres is al in gebruik' : user.email"
            :disabled="user.medewerker"
            data-testid="user-email"
        />

        <el-cascader
            class="w-full"
            v-model="user.roles"
            :options="roleOptions"
            :disabled="user.medewerker || roleOptions.length === 1"
            :props="{checkStrictly: true, emitPath: false}"
            clearable
            placeholder="rol"
            @change="onChange"
        />

        <!-- the div keeps the grid slot when the select is hidden -->
        <div data-testid="thema-org-select">
            <el-cascader
                v-if="heeftInstelling"
                class="w-full"
                v-model="orgThemeSelected"
                :options="orgThemeOptions"
                :disabled="user.medewerker"
                :props="{multiple: true}"
                clearable
                placeholder="instelling / thema"
                @change="onChangeOrgTheme"
            />
        </div>

        <!-- one wide pill with the auth details as small pills in two fixed slots: 2FA, and sessions or nieuw (a user with
             a session has logged in, so those two never meet); the whole thing opens the auth dialog -->
        <button
            type="button"
            class="inline-grid grid-cols-2 gap-0.5 items-center justify-self-start rounded-full border border-zzc-rand bg-white p-0.5 cursor-pointer hover:border-zzc-muted transition-colors"
            title="2FA, laatste login, wachtwoordmail en sessies"
            aria-label="2FA, laatste login, wachtwoordmail en sessies"
            data-testid="user-auth-button"
            @click="authDialog = true"
        >
            <span class="flex justify-center w-8 h-5" data-testid="two-factor-state">
                <StatusTag v-if="user.tweeFactor" kind="ok" class="w-full h-full inline-flex items-center justify-center !px-0" title="inloggen met een code per mail">
                    <span class="inline-flex items-center"><ShieldCheck :size="12" :stroke-width="2"/></span>
                </StatusTag>
            </span>
            <span class="flex justify-center w-8 h-5" data-testid="session-count">
                <StatusTag v-if="props.telling?.sessies" kind="info" class="w-full h-full inline-flex items-center justify-center !px-0" :title="sessieTitle">
                    <span class="inline-flex items-center gap-1"><Monitor :size="11" :stroke-width="2"/>{{ props.telling.sessies }}</span>
                </StatusTag>
                <StatusTag v-else-if="props.welkom && props.welkom !== 'wachtwoord'" :kind="WELKOM_KIND[props.welkom]" class="w-full h-full inline-flex items-center justify-center !px-0" :title="welkomTitle">
                    <span class="inline-flex items-center"><Gem :size="12" :stroke-width="2"/></span>
                </StatusTag>
            </span>
        </button>

        <StatusTag
            v-if="user.medewerker"
            kind="info"
            class="w-6 h-6 inline-flex items-center justify-center !px-0"
            title="medewerker: wordt op de server beheerd"
            aria-label="medewerker"
            data-testid="medewerker-mark"
        >
            <BadgeCheck :size="12" :stroke-width="2"/>
        </StatusTag>
        <IconButton
            v-else
            :icon="Trash2"
            label="permanent verwijderen"
            variant="danger"
            size="sm"
            data-testid="delete-button"
            @click="onDelete()"
        />
    </RowGrid>

    <UserAuthDialog
        v-model="authDialog"
        :user="user"
        :telling="props.telling"
        v-on:[USER_CHANGE_EMIT]="onChange"
        v-on:[USER_SESSIONS_ENDED_EMIT]="onSessionsEnded"
    />
</template>
